import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SyncPill } from "@/components/SyncPill";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { getPendingCount } from "@/lib/sync";
import { LogoutGuardModal } from "@/components/LogoutGuardModal";
import { LogOut, User, ChevronDown, Sun, Moon, Monitor } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export function Header({ subtitle }) {
  const { user, logout } = useAuth();
  const { resolved, toggle, theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const ThemeIcon = resolved === "dark" ? Moon : Sun;
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  useLocation();

  const handleLogoutClick = async () => {
    const pending = await getPendingCount();
    if (pending === 0) {
      await logout();
      navigate("/login", { replace: true });
    } else {
      setLogoutModalOpen(true);
    }
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-30 bg-background/85 backdrop-blur-md border-b border-border pt-safe"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 ring-focus rounded-lg -ml-1 px-1 py-0.5" data-testid="header-home-link">
          <Logo variant="mark" className="h-9 w-9 shrink-0" alt="Cuenta Vaca" />
          <div className="leading-tight">
            <div className="font-heading font-semibold text-foreground text-[15px] tracking-tight">
              <span>Cuenta</span>
              <span className="text-[hsl(var(--primary))] ml-1">Vaca</span>
            </div>
            <div className="text-[10.5px] text-muted-foreground uppercase tracking-[0.08em]">{subtitle || "Inventario · Identifica · Controla"}</div>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          <SyncPill />
          <button
            onClick={toggle}
            data-testid="header-theme-toggle"
            aria-label="Cambiar tema"
            className="h-9 w-9 grid place-items-center rounded-lg border border-border bg-card text-foreground hover:bg-muted transition-colors duration-150 ring-focus"
          >
            <ThemeIcon size={15} />
          </button>
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-user-menu"
                  className={cn(
                    "flex items-center gap-1.5 pl-1.5 pr-2 h-9 rounded-lg border border-border bg-card hover:bg-muted transition-colors duration-150 ring-focus"
                  )}
                >
                  <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground grid place-items-center">
                    <User size={13} />
                  </div>
                  <ChevronDown size={13} className="text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel className="text-xs">
                  <div className="font-semibold text-foreground">{user.name}</div>
                  <div className="text-muted-foreground font-normal">{user.email}</div>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-accent px-2 py-0.5 text-[10.5px] text-accent-foreground uppercase tracking-wide">
                    {user.role === "owner" ? "Dueño" : "Encargado"}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/ajustes/perfil" data-testid="header-profile-link">Mi perfil</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/ajustes" data-testid="header-settings-link">Ajustes</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10.5px] uppercase tracking-wide text-muted-foreground">Tema</DropdownMenuLabel>
                <div className="px-2 pb-2 flex gap-1">
                  {[
                    { v: "light", Icon: Sun, label: "Claro" },
                    { v: "dark", Icon: Moon, label: "Oscuro" },
                    { v: "system", Icon: Monitor, label: "Sistema" },
                  ].map((t) => (
                    <button
                      key={t.v}
                      data-testid={`header-theme-${t.v}`}
                      onClick={() => setTheme(t.v)}
                      className={cn(
                        "flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md border text-[11px] transition-colors duration-150",
                        theme === t.v
                          ? "border-primary text-primary bg-accent"
                          : "border-border text-foreground hover:bg-muted"
                      )}
                    >
                      <t.Icon size={12} /> {t.label}
                    </button>
                  ))}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogoutClick}
                  data-testid="header-logout-button"
                  className="text-destructive focus:text-destructive focus:bg-destructive/10"
                >
                  <LogOut size={13} className="mr-2" /> Salir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <LogoutGuardModal
        open={logoutModalOpen}
        onOpenChange={setLogoutModalOpen}
        onLoggedOut={() => navigate("/login", { replace: true })}
      />
    </header>
  );
}
